// ============================================================================
// RatingPrompt — the 2-step sentiment gate that decides whether to ask for
// an App Store rating. Fires from the Dashboard once per app-load if the
// backend says the user is eligible (server-side gating in
// /api/rating-prompt/eligibility — see routers/rating_prompt.py).
//
// Step 1: "How's Bilateral Calm feeling for you?"
//         → "It's helping"   → step 2A (App Store rating flow)
//         → "Not really"     → step 2B (private feedback textarea)
//
// Step 2A: On iOS, fires SKStoreReviewController via the Capacitor plugin
// (native in-app rating sheet). On web, opens the App Store review URL
// in a new tab. In both cases, the review submitted counts toward the
// public App Store rating.
//
// Step 2B: Textarea → POST /api/rating-prompt/feedback → emails admin.
// ============================================================================

import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { InAppReview } from "@capacitor-community/in-app-review";
import { Capacitor } from "@capacitor/core";
import { X, Heart, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { APP_STORE_URL } from "./OpenInAppCTA";
import logger from "../lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// App Store "leave a review" deep link — jumps straight into the write-review
// composer instead of the app page. Requires the numeric app ID (id{n}).
// Falls back to the marketing URL if we're not sure. Update both here and
// in OpenInAppCTA.jsx once you have the concrete id.
const APP_STORE_REVIEW_URL = `${APP_STORE_URL}?action=write-review`;

// Session flag so we ask the eligibility endpoint at most once per app load.
const SESSION_CHECKED_KEY = "bc_rating_prompt_checked_v1";

export const RatingPrompt = () => {
  const [step, setStep] = useState(null); // null | "sentiment" | "feedback"
  const [feedbackText, setFeedbackText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Fetch eligibility once per session. Server enforces all gating rules,
  // so we just trust its answer.
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_CHECKED_KEY)) return;
    sessionStorage.setItem(SESSION_CHECKED_KEY, "1");
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/rating-prompt/eligibility`);
        if (cancelled) return;
        if (res.data?.eligible) {
          // Delay 2s so the modal doesn't slam the user the moment the
          // dashboard finishes loading — feels less predatory.
          setTimeout(() => { if (!cancelled) setStep("sentiment"); }, 2000);
        }
      } catch (err) {
        logger.error("Rating prompt eligibility check failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const close = () => setStep(null);

  const recordOutcome = useCallback(async (outcome) => {
    try {
      await axios.post(`${API}/rating-prompt/record`, { outcome });
    } catch (err) {
      logger.error("Rating prompt record failed", err);
    }
  }, []);

  const handlePositive = async () => {
    await recordOutcome("sentiment_positive");
    // On iOS Capacitor, fire the native rating sheet.
    if (Capacitor.getPlatform() === "ios") {
      try {
        await InAppReview.requestReview();
      } catch (err) {
        // Some iOS versions throttle the native sheet silently. Fall back
        // to opening the App Store review URL so the user still has a path.
        logger.error("InAppReview failed, falling back to URL", err);
        window.open(APP_STORE_REVIEW_URL, "_blank", "noopener,noreferrer");
      }
    } else {
      // Web / Android — open the App Store review URL in a new tab.
      window.open(APP_STORE_REVIEW_URL, "_blank", "noopener,noreferrer");
    }
    close();
  };

  const handleNegative = async () => {
    await recordOutcome("sentiment_negative");
    setStep("feedback");
  };

  const submitFeedback = async () => {
    const text = feedbackText.trim();
    if (text.length < 3) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/rating-prompt/feedback`, { text });
      toast.success("Thank you — we read every message.");
      close();
    } catch (err) {
      logger.error("Feedback submit failed", err);
      toast.error("Couldn't send just now. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!step) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="button"
      tabIndex={0}
      data-testid="rating-prompt-overlay"
      onClick={close}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") close(); }}
    >
      <div
        className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl p-7 sm:p-8"
        role="document"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        data-testid="rating-prompt-modal"
      >
        <button
          onClick={close}
          className="absolute top-4 right-4 text-[#A0A6B4] hover:text-[#4A5568] p-1 rounded-full hover:bg-[#F6F4EF]"
          aria-label="Close"
          data-testid="rating-prompt-close"
        >
          <X className="w-5 h-5" />
        </button>

        {step === "sentiment" && (
          <div data-testid="rating-prompt-step-sentiment">
            <div className="w-12 h-12 rounded-full bg-[#3090A8]/10 flex items-center justify-center mb-4">
              <Heart className="w-6 h-6 text-[#3090A8]" />
            </div>
            <h2 className="font-outfit font-semibold text-xl sm:text-2xl text-[#1B2349] mb-3">
              How&rsquo;s Bilateral Calm feeling for you?
            </h2>
            <p className="text-[#4A5568] leading-relaxed mb-6">
              One quiet favor: if these sessions have helped, would you tell
              the App Store? Someone anxious tonight might be one search
              away from finding this.
            </p>
            <div className="flex flex-col gap-2.5">
              <Button
                onClick={handlePositive}
                className="w-full justify-center rounded-full bg-[#3090A8] hover:bg-[#267B90] text-white font-medium py-3"
                data-testid="rating-prompt-positive"
              >
                😌 It&rsquo;s helping
              </Button>
              <Button
                onClick={handleNegative}
                variant="outline"
                className="w-full justify-center rounded-full py-3"
                data-testid="rating-prompt-negative"
              >
                🌥 Not really
              </Button>
            </div>
          </div>
        )}

        {step === "feedback" && (
          <div data-testid="rating-prompt-step-feedback">
            <div className="w-12 h-12 rounded-full bg-[#F6F4EF] flex items-center justify-center mb-4">
              <MessageCircle className="w-6 h-6 text-[#4A5568]" />
            </div>
            <h2 className="font-outfit font-semibold text-xl sm:text-2xl text-[#1B2349] mb-3">
              That&rsquo;s the most useful thing you could tell us.
            </h2>
            <p className="text-[#4A5568] leading-relaxed mb-4">
              What&rsquo;s not landing? Even a sentence helps — we read every
              one, and it goes straight to the team.
            </p>
            <Textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Whatever comes to mind…"
              className="min-h-[120px] mb-4"
              maxLength={4000}
              data-testid="rating-prompt-feedback-text"
            />
            <div className="flex gap-2.5">
              <Button
                onClick={submitFeedback}
                disabled={submitting || feedbackText.trim().length < 3}
                className="flex-1 justify-center rounded-full bg-[#3090A8] hover:bg-[#267B90] text-white font-medium py-3"
                data-testid="rating-prompt-feedback-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send feedback"}
              </Button>
              <Button
                onClick={close}
                variant="outline"
                className="rounded-full px-5 py-3"
                data-testid="rating-prompt-feedback-skip"
              >
                Skip
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RatingPrompt;
